import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

// Palette Corporate Premium
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  bgLight: '#252529',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)',
};

const DroitAssurance = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTab, setActiveTab] = useState(0);
  
  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.3 });
  const { ref: servicesRef, inView: servicesInView } = useInView({ threshold: 0.2 });

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  const insuranceTypes = [
    {
      title: 'Assurance Vie',
      description: 'Contrats, clauses bénéficiaires et optimisation de la fiscalité.',
      icon: '💼'
    },
    {
      title: 'Assurance Santé',
      description: 'Couvertures, garanties, remboursements et gestion des litiges.',
      icon: '🏥'
    },
    {
      title: 'Responsabilité Civile',
      description: 'Protection professionnelle, civile et personnelle des dirigeants.',
      icon: '🛡️'
    },
    {
      title: 'Assurances Pro.',
      description: 'Analyse des risques d\'entreprise et structuration des garanties.',
      icon: '🏢'
    }
  ];

  const services = [
    {
      title: 'Rédaction de contrats',
      description: 'Le cabinet procède à l\'analyse rigoureuse et à la rédaction de polices d\'assurance sur mesure, adaptées aux spécificités de votre secteur d\'activité.',
      features: ['Rédaction de clauses sur mesure', 'Vérification de la conformité légale', 'Optimisation des plafonds de garantie']
    },
    {
      title: 'Gestion de sinistres',
      description: 'Nous vous accompagnons dès la déclaration du sinistre jusqu\'à l\'indemnisation finale, en veillant à la protection absolue de vos intérêts.',
      features: ['Assistance aux expertises de dommages', 'Négociation stratégique avec les assureurs', 'Contentieux et résolution des sinistres']
    },
    {
      title: 'Conseil réglementaire',
      description: 'Notre équipe assure votre conformité face aux évolutions complexes et constantes du droit national et européen des assurances.',
      features: ['Veille juridique personnalisée', 'Audits de conformité complets', 'Formation interne de vos équipes']
    }
  ];

  const computedStyles = useMemo(() => {
    const baseStyles = {
      section: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.bgDark} 0%, #17171A 50%, ${colors.bgDark} 100%)`,
        color: colors.beigeLight,
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      
      animatedBackground: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
      },
      
      noiseOverlay: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
        pointerEvents: 'none', zIndex: 1,
      },
      
      gradientSphere: {
        position: 'absolute', top: '10%', right: '-10%',
        width: '50vw', height: '50vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      // Hero Section
      hero: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '6rem 1.5rem' : isTablet ? '8rem 3rem' : '10rem 5%',
      },
      
      heroContent: {
        maxWidth: '1440px', margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
        gap: isMobile ? '3rem' : '5rem',
        alignItems: 'center',
      },
      
      titleLine: {
        height: '1px',
        background: `linear-gradient(90deg, ${colors.beigeMain}, transparent)`,
        marginBottom: isMobile ? '1.5rem' : '2rem',
        width: isMobile ? '60px' : '100px',
      },
      
      mainTitle: {
        fontSize: isMobile ? '2.5rem' : isTablet ? '3.2rem' : '4.5rem',
        lineHeight: 1.1,
        fontWeight: 300,
        marginBottom: isMobile ? '1rem' : '1.5rem',
        color: colors.beigeLight,
        letterSpacing: '-1px',
      },
      
      titleHighlight: {
        color: colors.beigeMain,
        fontStyle: 'italic',
      },
      
      subtitle: {
        fontSize: isMobile ? '1.1rem' : isTablet ? '1.3rem' : '1.6rem',
        color: colors.textGray,
        fontWeight: 300,
        display: 'block',
        marginTop: isMobile ? '0.5rem' : '1rem',
      },
      
      heroTextWrapper: {
        display: 'flex', flexDirection: 'column', gap: '1.5rem',
        marginBottom: '3rem', marginTop: '2rem',
      },
      
      textCard: {
        background: colors.bgMedium,
        padding: '1.5rem',
        borderLeft: `2px solid ${colors.beigeDark}`,
        display: 'flex', gap: '1.2rem', alignItems: 'flex-start',
      },
      
      cardIcon: {
        fontSize: '1.5rem', flexShrink: 0,
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)', // Effet Bronze/Beige
      },
      
      cardTitle: {
        fontSize: '1.1rem', fontWeight: 400, color: colors.beigeLight, marginBottom: '0.5rem', margin: 0,
      },
      
      heroText: {
        fontSize: '0.95rem', lineHeight: 1.6, color: colors.textGray, margin: 0, fontWeight: 300,
      },
      
      statsContainer: {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        padding: '2rem 0',
        borderTop: `1px solid ${colors.border}`,
      },
      
      statCard: {
        textAlign: isMobile ? 'center' : 'left', flex: 1,
        padding: isMobile ? '1rem 0' : '0 2rem',
      },
      
      statNumber: {
        fontSize: isMobile ? '2rem' : '2.5rem',
        fontWeight: 300, color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif",
        marginBottom: '0.2rem',
      },
      
      statLabel: {
        fontSize: '0.8rem', color: colors.textGray, textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      statDivider: {
        width: isMobile ? '50%' : '1px', height: isMobile ? '1px' : '50px', background: colors.border,
      },
      
      heroImageContainer: {
        position: 'relative', padding: '20px',
      },
      
      imageBackground: {
        position: 'absolute', top: '20px', left: '20px', right: '-20px', bottom: '-20px',
        border: `1px solid ${colors.beigeDark}`,
        borderRadius: '2px', zIndex: -1,
      },
      
      imageWrapper: {
        position: 'relative',
        height: isMobile ? '400px' : isTablet ? '500px' : '650px',
        borderRadius: '2px', overflow: 'hidden',
      },
      
      image: {
        width: '100%', height: '100%', objectFit: 'cover',
        filter: 'grayscale(25%) contrast(1.1) brightness(0.85)',
      },
      
      imageOverlay: {
        position: 'absolute', inset: 0,
        background: `linear-gradient(to top, rgba(18,18,20,0.9) 0%, transparent 50%)`,
      },
      
      imageBadge: {
        position: 'absolute', bottom: '0', left: '0',
        background: colors.bgMedium,
        padding: '1.5rem 2rem',
        borderTop: `1px solid ${colors.border}`,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', gap: '1.2rem', zIndex: 2,
      },
      
      badgeText: {
        display: 'flex', flexDirection: 'column', gap: '0.2rem',
      },
      
      badgeTitle: {
        color: colors.beigeMain, fontSize: '1rem', fontWeight: 400, fontFamily: "'Playfair Display', serif",
      },
      
      badgeSubtitle: {
        color: colors.textGray, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      // Insurance Types Section
      typesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight,
        borderTop: `1px solid ${colors.border}`,
      },
      
      sectionHeader: {
        textAlign: 'center', marginBottom: isMobile ? '3rem' : '5rem',
      },
      
      sectionTitle: {
        fontSize: isMobile ? '2rem' : '3rem',
        fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1rem', letterSpacing: '-0.5px',
      },
      
      sectionSubtitle: {
        fontSize: '1.1rem', color: colors.textGray,
        maxWidth: '600px', margin: '0 auto', fontWeight: 300,
      },
      
      typesGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '1.5rem', maxWidth: '1440px', margin: '0 auto',
      },
      
      typeCard: {
        background: colors.bgMedium,
        borderRadius: '2px', padding: '2.5rem',
        border: `1px solid ${colors.border}`,
        display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      },
      
      typeNumber: {
        position: 'absolute', top: '1.5rem', right: '1.5rem',
        fontSize: '2rem', fontWeight: 300, color: colors.beigeMain, opacity: 0.2,
        fontFamily: "'Playfair Display', serif",
      },
      
      typeIcon: {
        fontSize: '2rem', marginBottom: '1.5rem',
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)',
      },
      
      typeTitle: {
        fontSize: '1.2rem', fontWeight: 400, color: colors.beigeLight, marginBottom: '1rem',
      },
      
      typeDescription: {
        fontSize: '0.95rem', color: colors.textGray, lineHeight: 1.6, flex: 1, fontWeight: 300,
      },
      
      typeButton: {
        marginTop: '1.5rem', padding: 0,
        background: 'transparent', border: 'none',
        color: colors.beigeMain, fontSize: '0.9rem', fontWeight: 400,
        textTransform: 'uppercase', letterSpacing: '1px',
        textAlign: 'left', cursor: 'pointer', display: 'inline-block',
      },
      
      // Services Tabs Section
      servicesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
      },
      
      tabsContainer: {
        maxWidth: '1000px', margin: '0 auto',
      },
      
      tabsHeader: {
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        borderBottom: `1px solid ${colors.border}`, marginBottom: '3rem',
      },
      
      tabButton: {
        flex: 1, padding: '1.2rem 1rem',
        background: 'transparent', border: 'none',
        color: colors.textGray, fontSize: '1.1rem', fontWeight: 400,
        cursor: 'pointer', transition: 'all 0.3s ease',
        borderBottom: '2px solid transparent',
        marginBottom: '-1px', // Overlay border
      },
      
      activeTab: {
        color: colors.beigeMain,
        borderBottom: `2px solid ${colors.beigeMain}`,
      },
      
      tabContent: {
        background: colors.bgMedium,
        border: `1px solid ${colors.border}`,
        borderRadius: '2px', padding: isMobile ? '2rem 1.5rem' : '4rem',
      },
      
      tabTitle: {
        fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1.5rem', fontFamily: "'Playfair Display', serif",
      },
      
      tabDescription: {
        fontSize: '1.05rem', color: colors.textGray, lineHeight: 1.8,
        marginBottom: '2.5rem', fontWeight: 300,
      },
      
      tabFeatures: {
        listStyle: 'none', padding: 0, margin: '0 0 3rem 0',
      },
      
      tabFeature: {
        color: colors.beigeLight, marginBottom: '1rem',
        display: 'flex', alignItems: 'flex-start', gap: '1rem',
        fontSize: '1rem', fontWeight: 300,
      },
      
      featureDot: {
        width: '12px', height: '1px', backgroundColor: colors.beigeDark, marginTop: '10px', flexShrink: 0,
      },
      
      tabActionButton: {
        background: 'transparent',
        border: `1px solid ${colors.beigeDark}`,
        color: colors.beigeLight,
        padding: '1rem 2.5rem',
        fontSize: '0.9rem', fontWeight: 400, letterSpacing: '1px', textTransform: 'uppercase',
        cursor: 'pointer', transition: 'all 0.3s ease',
      },
      
      // CTA Section
      ctaSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight, borderTop: `1px solid ${colors.border}`,
      },
      
      ctaContainer: {
        maxWidth: '1000px', margin: '0 auto', textAlign: 'center',
      },
      
      ctaTitle: {
        fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1rem', letterSpacing: '-0.5px',
      },
      
      ctaText: {
        fontSize: '1.1rem', color: colors.textGray, lineHeight: 1.6,
        maxWidth: '600px', margin: '0 auto 3rem', fontWeight: 300,
      },
      
      ctaButtons: {
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        gap: '1.5rem', justifyContent: 'center', alignItems: 'center',
      },
      
      ctaButtonPrimary: {
        background: colors.beigeMain, color: colors.bgDark, border: 'none',
        padding: '1rem 2.5rem', borderRadius: '2px',
        fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px',
        cursor: 'pointer', transition: 'all 0.3s ease', width: isMobile ? '100%' : 'auto',
      },
      
      ctaButtonSecondary: {
        background: 'transparent', border: `1px solid ${colors.beigeDark}`, color: colors.beigeLight,
        padding: '1rem 2.5rem', borderRadius: '2px',
        fontSize: '0.9rem', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '1px',
        cursor: 'pointer', transition: 'all 0.3s ease', width: isMobile ? '100%' : 'auto',
      },
    };

    return baseStyles;
  }, [isMobile, isTablet]);

  // Animations Premium (Courbes de Bézier douces)
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const fadeUpVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
    exit: { opacity: 0, y: -15, transition: { duration: 0.3 } }
  };

  return (
    <>
      <Helmet>
        <title>Droit des Assurances | KMJ & Associés</title>
        <meta name="description" content="Expertise en droit des assurances : contrats, sinistres, conseil réglementaire. Protection juridique optimale pour vos assurances." />
      </Helmet>

      <motion.section 
        style={computedStyles.section}
        initial="hidden" animate="visible" variants={containerVariants}
      >
        {/* Background Overlay Minimaliste */}
        <div style={computedStyles.animatedBackground}>
          <div style={computedStyles.gradientSphere} />
          <div style={computedStyles.noiseOverlay} />
        </div>

        {/* Hero Section */}
        <motion.div style={computedStyles.hero} ref={heroRef} variants={containerVariants}>
          <div style={computedStyles.heroContent}>
            <motion.div variants={fadeUpVariants}>
              <div style={computedStyles.titleLine} />
              <h1 style={computedStyles.mainTitle}>
                Droit des
                <br />
                <span style={computedStyles.titleHighlight}>assurances.</span>
              </h1>
              <span style={computedStyles.subtitle}>
                Protection & Sécurité juridique.
              </span>

              <motion.div style={computedStyles.heroTextWrapper}>
                <div style={computedStyles.textCard}>
                  <div style={computedStyles.cardIcon}>📋</div>
                  <div>
                    <h4 style={computedStyles.cardTitle}>Expertise Contractuelle</h4>
                    <p style={computedStyles.heroText}>
                      Le cabinet offre une expertise pointue dans l'analyse, la rédaction et la mise en conformité de vos polices d'assurance.
                    </p>
                  </div>
                </div>
                <div style={computedStyles.textCard}>
                  <div style={computedStyles.cardIcon}>⚖️</div>
                  <div>
                    <h4 style={computedStyles.cardTitle}>Gestion de Sinistres</h4>
                    <p style={computedStyles.heroText}>
                      Nous vous assistons stratégiquement lors de sinistres, dans la gestion des indemnisations et les litiges éventuels.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Stats Bar */}
              <motion.div style={computedStyles.statsContainer}>
                <div style={computedStyles.statCard}>
                  <div style={computedStyles.statNumber}>500+</div>
                  <div style={computedStyles.statLabel}>Polices analysées</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statCard}>
                  <div style={computedStyles.statNumber}>300+</div>
                  <div style={computedStyles.statLabel}>Litiges résolus</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statCard}>
                  <div style={computedStyles.statNumber}>98%</div>
                  <div style={computedStyles.statLabel}>Satisfaction client</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image & Offset Frame */}
            <motion.div style={computedStyles.heroImageContainer} variants={fadeUpVariants}>
              <div style={computedStyles.imageBackground} />
              <div style={computedStyles.imageWrapper}>
                <img src="/images/L9.jpg" alt="Droit des assurances" style={computedStyles.image} loading="lazy" />
                <div style={computedStyles.imageOverlay} />
                <div style={computedStyles.imageBadge}>
                  <div style={computedStyles.cardIcon}>🛡️</div>
                  <div style={computedStyles.badgeText}>
                    <div style={computedStyles.badgeTitle}>Pôle Assurances</div>
                    <div style={computedStyles.badgeSubtitle}>Protection juridique</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Insurance Types Section */}
        <div style={computedStyles.typesSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Domaines <span style={computedStyles.titleHighlight}>d'intervention</span>
            </motion.h2>
            <motion.p style={computedStyles.sectionSubtitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Une expertise transversale couvrant l'ensemble des branches de l'assurance.
            </motion.p>
          </div>

          <div style={computedStyles.typesGrid}>
            {insuranceTypes.map((type, index) => (
              <motion.div
                key={index} style={computedStyles.typeCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark }}
              >
                <div style={computedStyles.typeNumber}>0{index + 1}</div>
                <div style={computedStyles.typeIcon}>{type.icon}</div>
                <h3 style={computedStyles.typeTitle}>{type.title}</h3>
                <p style={computedStyles.typeDescription}>{type.description}</p>
                <button style={computedStyles.typeButton}>En savoir plus →</button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Tabs Section */}
        <div style={computedStyles.servicesSection} ref={servicesRef}>
          <div style={computedStyles.tabsContainer}>
            <div style={computedStyles.sectionHeader}>
              <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
                Notre <span style={computedStyles.titleHighlight}>Accompagnement</span>
              </motion.h2>
            </div>

            <div style={computedStyles.tabsHeader}>
              {services.map((service, index) => (
                <button
                  key={index}
                  style={{ ...computedStyles.tabButton, ...(activeTab === index ? computedStyles.activeTab : {}) }}
                  onClick={() => setActiveTab(index)}
                >
                  {service.title}
                </button>
              ))}
            </div>

            <div style={computedStyles.tabContentWrapper}>
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeTab} style={computedStyles.tabContent}
                  variants={tabVariants} initial="hidden" animate="visible" exit="exit"
                >
                  <h3 style={computedStyles.tabTitle}>{services[activeTab].title}</h3>
                  <p style={computedStyles.tabDescription}>{services[activeTab].description}</p>
                  <ul style={computedStyles.tabFeatures}>
                    {services[activeTab].features.map((feature, i) => (
                      <li key={i} style={computedStyles.tabFeature}>
                        <div style={computedStyles.featureDot} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button style={computedStyles.tabActionButton} onMouseOver={(e) => e.target.style.background = 'rgba(212, 197, 176, 0.1)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
                    Solliciter une consultation
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div style={computedStyles.ctaSection}>
          <motion.div 
            style={computedStyles.ctaContainer}
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}
          >
            <div style={computedStyles.cardIcon}>⚖️</div>
            <h2 style={computedStyles.ctaTitle}>
              Besoin d'un conseil <span style={computedStyles.titleHighlight}>en assurances ?</span>
            </h2>
            <p style={computedStyles.ctaText}>
              Nos avocats experts sécurisent vos intérêts professionnels et personnels face à la complexité des contrats d'assurance.
            </p>
            <div style={computedStyles.ctaButtons}>
              <button style={computedStyles.ctaButtonPrimary}>Prendre rendez-vous</button>
              <button style={computedStyles.ctaButtonSecondary} onMouseOver={(e) => e.target.style.background = 'rgba(212, 197, 176, 0.1)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
                Nous contacter
              </button>
            </div>
          </motion.div>
        </div>

      </motion.section>
    </>
  );
};

export default DroitAssurance;